'use client';

import { useState } from 'react';
import Image from 'next/image';
import { X, ChevronLeft, ChevronRight, Images } from 'lucide-react';
import { MemorialImage } from '@/lib/types';

interface PhotoGalleryProps {
  images: MemorialImage[];
}

export function PhotoGallery({ images }: PhotoGalleryProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const releasedImages = images.filter(img => img.isReleased && img.image);

  if (releasedImages.length === 0) return null;

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? releasedImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === releasedImages.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
  };

  return (
    <section className="py-12 bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Images className="w-6 h-6 text-memorial-secondary" />
          <h2 className="font-serif text-2xl md:text-3xl text-memorial-primary">
            Bildergalerie
          </h2>
        </div>

        {/* Image grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {releasedImages.map((image, index) => (
            <button
              key={image.id}
              onClick={() => openLightbox(index)}
              className="group relative aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-memorial-primary focus:ring-offset-2"
            >
              <Image
                src={image.image!}
                alt={image.name || 'Galeriebild'}
                fill
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              {/* Caption */}
              {image.name && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-sm truncate">{image.name}</p>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Lightbox */}
        {lightboxOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="dialog"
            aria-modal="true"
            aria-label="Bildansicht"
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-colors z-10"
              aria-label="Schließen"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Navigation buttons */}
            {releasedImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                  className="absolute left-4 p-2 text-white/80 hover:text-white transition-colors"
                  aria-label="Vorheriges Bild"
                >
                  <ChevronLeft className="w-10 h-10" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  className="absolute right-4 p-2 text-white/80 hover:text-white transition-colors"
                  aria-label="Nächstes Bild"
                >
                  <ChevronRight className="w-10 h-10" />
                </button>
              </>
            )}

            {/* Current image */}
            <div
              className="relative max-w-4xl max-h-[80vh] w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <Image
                src={releasedImages[currentIndex].image!}
                alt={releasedImages[currentIndex].name || 'Galeriebild'}
                width={1200}
                height={800}
                className="object-contain max-h-[80vh] w-auto mx-auto"
              />
              {/* Caption */}
              {(releasedImages[currentIndex].name || releasedImages[currentIndex].description) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-center">
                  {releasedImages[currentIndex].name && (
                    <p className="text-white text-lg font-medium">{releasedImages[currentIndex].name}</p>
                  )}
                  {releasedImages[currentIndex].description && (
                    <p className="text-white/80 text-sm mt-1">{releasedImages[currentIndex].description}</p>
                  )}
                </div>
              )}
            </div>

            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              {currentIndex + 1} / {releasedImages.length}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
